import * as React from "react";
import { Eye, EyeOff, TriangleAlert } from "./icons";

import { Input, type InputProps } from "./Input";
import { IconButton } from "./IconButton";

export type PasswordInputProps = Omit<InputProps, "type" | "trailing">;

/** Password field with a show/hide toggle and a Caps Lock warning. */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ onKeyUp, onKeyDown, onBlur, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);
    const [capsLock, setCapsLock] = React.useState(false);

    const trackCapsLock = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (typeof event.getModifierState === "function") {
        setCapsLock(event.getModifierState("CapsLock"));
      }
    };

    return (
      <div data-ui="password-input" className="flex flex-col gap-1.5">
        <Input
          ref={ref}
          type={visible ? "text" : "password"}
          onKeyDown={(event) => {
            trackCapsLock(event);
            onKeyDown?.(event);
          }}
          onKeyUp={(event) => {
            trackCapsLock(event);
            onKeyUp?.(event);
          }}
          onBlur={(event) => {
            setCapsLock(false);
            onBlur?.(event);
          }}
          trailing={
            <IconButton
              size="sm"
              label={visible ? "Hide password" : "Show password"}
              aria-pressed={visible}
              hideTooltip
              className="text-muted-foreground hover:text-foreground"
              icon={visible ? <EyeOff /> : <Eye />}
              onClick={() => setVisible((value) => !value)}
            />
          }
          {...props}
        />
        {capsLock && (
          <p role="status" className="flex items-center gap-1.5 text-xs text-warning-text">
            <TriangleAlert aria-hidden="true" className="h-3.5 w-3.5" />
            Caps Lock is on
          </p>
        )}
      </div>
    );
  }
);
PasswordInput.displayName = "PasswordInput";
